import { c as _c } from "react/compiler-runtime";
import { MaterialIcon } from "@website/icons/material";
import { ButtonIconColorModeType } from "@website/ui/button";
import { deepOmit } from "@website/utils/deep-omit";
import { isFilled } from "@website/utils/is-filled";
import { numberConvert } from "@website/utils/number-convert";
import { CheckboxFilter } from "@website/widget/filter/filter-components/checkbox";
import { RadioFilter } from "@website/widget/filter/filter-components/radio";
import { RangeFilter } from "@website/widget/filter/filter-components/range";
import { StripFilter } from "@website/widget/filter/filter-components/strip";
import { SwitchFilter } from "@website/widget/filter/filter-components/switch";
import { HotelFilterChipsPropertiesTypes } from "@website/widget/filter/hotel-filter-chips/hotel-filter-chips.types";
import type { ItemStaggerPropertiesGetter } from "@website/widget/item-stagger/item-stagger.hook";
import omit from "lodash-es/omit";
import { useRouter } from "next/router";
import { isValidElement, MouseEvent, ReactElement, ReactNode, useState } from "react";
type DraftRangeValues = Record<string, [number, number]>;
type FilterChildProperties = {
  title?: string;
};
type HotelFiltersChipsHookProperties = HotelFilterChipsPropertiesTypes & {
  getStripItemStaggerProperties?: ItemStaggerPropertiesGetter;
  stripItemStaggerIndexes?: Record<string, number>;
};
const isFilterChild = (child: ReactNode): child is ReactElement<FilterChildProperties> => isValidElement(child);
const isPriceRangeFilterFilled = (query: Record<string, unknown>) => query["price_from"] || query["price_to"];
const isFilterFilled = (key: string, query: Record<string, unknown>) => key === "price_range" ? isPriceRangeFilterFilled(query) : query[key];
const getFilterResetKeys = (key: string) => key === "price_range" ? ["price_from", "price_to"] : key === "nearby" ? ["nearby", "geo_property_summary"] : [key];
export const useHotelFiltersChips = t0 => {
  const $ = _c(43);
  const {
    chipsFiltersSchema,
    deferRangeUpdates,
    getStripItemStaggerProperties,
    hidePopoverActions,
    selectedFiltersCount,
    setFilterOpen,
    stripItemStaggerIndexes
  } = t0;
  const router = useRouter();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [draftRangeValues, setDraftRangeValues] = useState(t1);
  const shouldDeferRangeUpdates = deferRangeUpdates ?? !hidePopoverActions;
  let children;
  let defaultStripKey;
  let t2;
  if ($[1] !== chipsFiltersSchema || $[2] !== getStripItemStaggerProperties || $[3] !== router || $[4] !== shouldDeferRangeUpdates || $[5] !== stripItemStaggerIndexes) {
    const filtersSchemaEntries = Object.entries(chipsFiltersSchema);
    children = filtersSchemaEntries.map(t3 => {
      const [name, schema] = t3;
      switch (schema.type) {
        case "range":
          {
            return <RangeFilter key={name} {...schema} onValueChange={shouldDeferRangeUpdates ? value => setDraftRangeValues(previous => ({
              ...previous,
              [name]: value
            })) : undefined} showTitle={false} updateQueryOnChange={!shouldDeferRangeUpdates} />;
          }
        case "checkbox":
          {
            return <CheckboxFilter collapseClassName="!overflow-auto" containerClassName={schema?.containerClassName} key={name} name={name} {...schema} isNew={false} showTitle={false} />;
          }
        case "strip":
          {
            return <StripFilter getItemStaggerProperties={getStripItemStaggerProperties} itemStaggerIndexes={stripItemStaggerIndexes} key={name} name={name} {...schema} />;
          }
        case "radio":
          {
            return <RadioFilter key={name} name={name} {...schema} isNew={false} showTitle={false} />;
          }
        case "switch":
          {
            return <SwitchFilter key={name} name={name} {...schema} />;
          }
        case "text":
          {
            return null;
          }
        default:
          {
            return null;
          }
      }
    }).filter(isFilterChild);
    defaultStripKey = filtersSchemaEntries?.find(_temp)?.[0];
    const filterChildrenByKey = Object.fromEntries(children.map(_temp2));
    t2 = filtersSchemaEntries?.map(t4 => {
      const [key, values_0] = t4;
      const selectedChild = filterChildrenByKey[key];
      return {
        hasValue: Boolean(isFilterFilled(key, router.query)),
        icon: "icon" in values_0 ? values_0.icon : undefined,
        isOpen: router?.query.popover === key,
        isStripFilter: values_0.type === "strip",
        isTextFilter: values_0.type === "text",
        key,
        popoverTitle: selectedChild?.props?.title,
        resetKeys: getFilterResetKeys(key),
        selectedChild,
        shouldExpandPopover: key === "facilities",
        title: "title" in values_0 ? values_0.title : ""
      };
    });
    $[1] = chipsFiltersSchema;
    $[2] = getStripItemStaggerProperties;
    $[3] = router;
    $[4] = shouldDeferRangeUpdates;
    $[5] = stripItemStaggerIndexes;
    $[6] = children;
    $[7] = defaultStripKey;
    $[8] = t2;
  } else {
    children = $[6];
    defaultStripKey = $[7];
    t2 = $[8];
  }
  const chips = t2;
  let t3;
  if ($[9] !== router) {
    t3 = (filterKeys, event) => {
      event?.stopPropagation();
      return router.replace({
        pathname: router?.pathname,
        query: deepOmit({
          ...router?.query,
          popover: filterKeys?.length > 1 ? "price" : filterKeys?.[0]
        }, isFilled)
      }, undefined, {
        shallow: true,
        scroll: false
      });
    };
    $[9] = router;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const onClick = t3;
  let t4;
  if ($[11] !== router) {
    t4 = (filterKey, event_0) => {
      event_0?.stopPropagation();
      return router.replace({
        pathname: router?.pathname,
        query: deepOmit({
          ...(filterKey === "nearby" ? omit(router.query, ["geo_property_summary", "popover"]) : omit(router.query, ["popover"])),
          [filterKey]: !router.query?.[filterKey] || undefined
        }, isFilled)
      }, undefined, {
        shallow: true,
        scroll: false
      });
    };
    $[11] = router;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const onTextClick = t4;
  let t5;
  if ($[13] !== router) {
    t5 = (filterKeys_0, event_1) => {
      event_1?.preventDefault();
      event_1?.stopPropagation();
      const currentPopover = String(router?.query?.popover || "");
      const draftKeysToClear = [...filterKeys_0, currentPopover].filter(Boolean);
      if (draftKeysToClear.length > 0) {
        setDraftRangeValues(previous_0 => omit(previous_0, draftKeysToClear) as DraftRangeValues);
      }
      return router.replace({
        pathname: router?.pathname,
        query: deepOmit(omit(router?.query, [...filterKeys_0, "popover"]), isFilled)
      }, undefined, {
        shallow: true,
        scroll: false
      });
    };
    $[13] = router;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const onClose = t5;
  let t6;
  if ($[15] !== onClose || $[16] !== router?.query?.popover) {
    t6 = () => {
      const currentPopover_0 = String(router?.query?.popover || "");
      return onClose([currentPopover_0, ...(currentPopover_0 === "price_range" ? ["price_from", "price_to"] : [])]);
    };
    $[15] = onClose;
    $[16] = router?.query?.popover;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const onReset = t6;
  let t7;
  if ($[18] !== chipsFiltersSchema || $[19] !== draftRangeValues || $[20] !== onClose || $[21] !== router || $[22] !== shouldDeferRangeUpdates) {
    t7 = event_2 => {
      event_2?.preventDefault();
      event_2?.stopPropagation();
      const currentPopover_1 = String(router?.query?.popover || "");
      const schema_0 = chipsFiltersSchema[currentPopover_1];
      if (shouldDeferRangeUpdates && schema_0?.type === "range") {
        const rangeMin = Number(schema_0.min ?? 0);
        const rangeMax = Number(schema_0.max ?? 0);
        const [minValue, maxValue] = draftRangeValues[currentPopover_1] ?? [Number(router.query[schema_0.min_key] || rangeMin), Number(router.query[schema_0.max_key] || rangeMax)];
        setDraftRangeValues(previous_1 => omit(previous_1, [currentPopover_1]) as DraftRangeValues);
        return router.replace({
          pathname: router?.pathname,
          query: deepOmit({
            ...router.query,
            popover: undefined,
            [schema_0.min_key]: Number(minValue) === rangeMin ? undefined : minValue,
            [schema_0.max_key]: Number(maxValue) === rangeMax ? undefined : maxValue
          }, isFilled)
        }, undefined, {
          shallow: true,
          scroll: false
        });
      }
      return onClose([], event_2);
    };
    $[18] = chipsFiltersSchema;
    $[19] = draftRangeValues;
    $[20] = onClose;
    $[21] = router;
    $[22] = shouldDeferRangeUpdates;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const onSubmit = t7;
  const t8 = selectedFiltersCount ? "secondary" as const : "neutral" as const;
  const t9 = selectedFiltersCount && selectedFiltersCount > 0 ? "inherit" : "custom" as ButtonIconColorModeType;
  let t10;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <MaterialIcon variant="outlined" className="text-on-surface-medium-emphasis h-[24px] w-[18px]" name="tune" />;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== router || $[26] !== selectedFiltersCount) {
    t11 = selectedFiltersCount ? ` همه فیلترها (${numberConvert(selectedFiltersCount, {
      locale: router.locale
    })})` : "\u0647\u0645\u0647 \u0641\u06CC\u0644\u062A\u0631\u0647\u0627";
    $[25] = router;
    $[26] = selectedFiltersCount;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== setFilterOpen || $[29] !== t11 || $[30] !== t8 || $[31] !== t9) {
    t12 = {
      variant: "round-outline" as const,
      color: t8,
      size: "small" as const,
      iconColorMode: t9,
      StartIcon: t10,
      children: t11,
      onClick: setFilterOpen
    };
    $[28] = setFilterOpen;
    $[29] = t11;
    $[30] = t8;
    $[31] = t9;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== children || $[34] !== chips || $[35] !== defaultStripKey || $[36] !== onClick || $[37] !== onClose || $[38] !== onReset || $[39] !== onSubmit || $[40] !== onTextClick || $[41] !== t12) {
    t13 = {
      chips,
      children,
      defaultStripKey,
      onClick,
      onClose,
      onSubmit,
      onTextClick,
      onReset,
      buttonProperties: t12
    };
    $[33] = children;
    $[34] = chips;
    $[35] = defaultStripKey;
    $[36] = onClick;
    $[37] = onClose;
    $[38] = onReset;
    $[39] = onSubmit;
    $[40] = onTextClick;
    $[41] = t12;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  return t13;
};
function _temp(t0) {
  const [, values] = t0;
  return values.type === "strip";
}
function _temp2(child) {
  return [String(child?.key), child];
}